import React, { useMemo, useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { createChartData, createChartOptions } from "../constants/chartConfig";

/**
 * Performance Chart Component
 * Displays historical performance data in a line chart
 * Limits data points for better mobile/desktop experience
 */
const PerformanceChart = ({ sessions, findDartById }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Calculate items per page based on screen size
  const itemsPerPage = isMobile ? 10 : 20;

  // Calculate pagination
  const totalPages = Math.ceil(sessions.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sessions.length);

  // Get sessions for current page (most recent first)
  const paginatedSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    // Sort sessions by date (most recent first) and paginate
    const sortedSessions = [...sessions].sort((a, b) => {
      const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
      const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
      return dateB - dateA; // Most recent first
    });

    return sortedSessions.slice(startIndex, endIndex);
  }, [sessions, startIndex, endIndex]);

  // Memoize chart data and options for performance
  const chartData = useMemo(
    () => createChartData(paginatedSessions, t),
    [paginatedSessions, t]
  );

  const chartOptions = useMemo(
    () => createChartOptions(t, paginatedSessions, findDartById),
    [t, paginatedSessions, findDartById]
  );

  // Navigation handlers
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToFirstPage = () => setCurrentPage(0);

  if (!sessions || sessions.length <= 1) {
    return (
      <div className="bg-primary/80 rounded-xl p-4 border border-primary/30 flex items-center justify-center h-32">
        <p className="text-white text-center">
          {t("history.saveMoreSessions")}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-primary/80 rounded-xl p-4 border border-primary/30">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
        <h3 className="text-lg font-medium text-test mb-2 sm:mb-0">
          {t("history.performanceOverTime")}
        </h3>
        {totalPages > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-white/70">
              {t("history.showingResults", {
                start: startIndex + 1,
                end: endIndex,
                total: sessions.length,
              })}
            </span>
          </div>
        )}
      </div>

      <div className="h-80 lg:h-96 relative">
        <Line data={chartData} options={chartOptions} />
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-white/10">
          <button
            onClick={goToFirstPage}
            disabled={currentPage === 0}
            className="px-3 py-1 text-sm bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t("history.latest")}
          </button>
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 0}
            className="px-3 py-1 text-sm bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ←
          </button>
          <span className="text-white/70 text-sm px-2">
            {currentPage + 1} / {totalPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages - 1}
            className="px-3 py-1 text-sm bg-white/10 text-white rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};

export default PerformanceChart;
